import _ from 'lodash';
import React from 'react';

// Material UI
import FormControl from '@material-ui/core/FormControl/FormControl';

// Components
import { FieldCaption } from '../_ui/field-caption.component';
import { FormHelperText, FormInput } from '../_ui/forms.component';
import { FormikFieldLabel } from './formik-field-label.component';
import { PasswordReveal } from './password-reveal.component';
import { FormikFieldCommonProps, useFormikField } from './use-formik-field';

interface FormikInputProps extends FormikFieldCommonProps {
  uncontrolled?: boolean;
  margin?: 'none' | 'dense' | 'normal';
  type?: string;
  disabled?: boolean;
  // Persistent hint describing the field. Renders above any validation error so
  // the layout doesn't shift as the error appears and clears.
  hint?: React.ReactNode;
}

export const FormikInput: React.FC<FormikInputProps> = ({ field, margin, fid, id, label, form, uncontrolled, type, disabled, hint, ...rest }) => {
  const { id: _id, error, touched } = useFormikField({ field, form, fid, id });
  const [revealed, setRevealed] = React.useState(false);
  const onToggleReveal = React.useCallback(() => setRevealed(curr => !curr), []);
  const isPassword = type === 'password';
  const showError = Boolean(touched) && _.isString(error);
  const hintId = hint ? `${_id}-helper` : undefined;
  const errorId = showError ? `${_id}-error` : undefined;
  // aria-describedby takes a list, so the input names every description it
  // currently has — an active error is announced as well as the hint.
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  const patch: any = { ...rest };
  if (!uncontrolled) {
    patch.value = field.value;
    delete patch.defaultValue;
  }

  const input = (
    <FormInput
      id={_id}
      type={isPassword && revealed ? 'text' : (type || 'text')}
      name={field.name}
      onChange={field.onChange}
      onBlur={field.onBlur}
      disabled={disabled}
      aria-describedby={describedBy}
      aria-invalid={showError || undefined}
      $hasError={showError}
      // Session recorders only auto-obfuscate type="password"; revealing flips
      // the type, so a password field opts out of recording unconditionally.
      data-private={isPassword ? 'redact' : undefined}
      {...patch}
    />
  );

  return (
    <FormControl margin={margin || 'normal'} fullWidth component="div" error={!!error && touched}>
      <FormikFieldLabel label={label} htmlFor={_id} hasError={showError} />
      {isPassword
        ? <PasswordReveal revealed={revealed} onToggle={onToggleReveal}>{input}</PasswordReveal>
        : input}
      {hint && <FieldCaption id={hintId}>{hint}</FieldCaption>}
      {showError && <FormHelperText id={errorId} error>{error}</FormHelperText>}
    </FormControl>
  );
}
